import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';
import {
  Calendar as CalendarIcon,
  Check,
  Info,
  Loader2,
  Minus,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Calendar } from '@/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  formatDateRangeLabel,
  isoToLocalDate,
  localDateToIso,
} from '@/utils/dateRangePicker';
import { adsApi } from '@/api/client';
import {
  REPORT_AD_PRODUCT_OPTIONS,
  reportAdProductsQueryParam,
} from '@/api/reportAdProducts';
import {
  getMetricValue,
  percentChangeVsBaseline,
  REPORT_COMPARE_METRICS,
  type CompareMetricDef,
} from '@/api/reportCompareMetrics';
import {
  getPreviousMonthVsPriorMonth,
  isRangeValid,
  type DateRange,
} from '@/api/reportDateRanges';
import { parseReportSummaryPayload, type ReportSummaryParsed } from '@/api/reportSummary';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

function formatCurrency(amount: number) {
  return formatAccountCurrency(amount, undefined, { empty: '-' });
}

function formatNumberValue(value: number | null, fractionDigits = 2): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function formatPercentValue(value: number | null): string {
  if (value === null) return '-';
  return `${value.toFixed(2)}%`;
}

function formatCurrencyValue(value: number | null): string {
  if (value === null) return '-';
  return formatCurrency(value);
}

function formatMetricCell(def: CompareMetricDef, value: number | null): string {
  if (value === null) return '-';
  switch (def.kind) {
    case 'int':
      return formatNumberValue(value, 0);
    case 'currency':
      return formatCurrencyValue(value);
    case 'percent':
      return formatPercentValue(value);
    case 'ratio':
      return formatNumberValue(value, 2);
    default:
      return String(value);
  }
}

function formatPctChangeLabel(result: ReturnType<typeof percentChangeVsBaseline>): string {
  if (result.type === 'both_zero') return '0.0%';
  if (result.type === 'baseline_zero') return '—';
  const v = result.value;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

function formatComparisonRangeSummary(periodA: DateRange, periodB: DateRange): string {
  return `${formatDateRangeLabel(periodA.startDate, periodA.endDate)} vs ${formatDateRangeLabel(periodB.startDate, periodB.endDate)}`;
}

function selectedAdTypeLabels(selectedIds: string[]): string {
  const labels = REPORT_AD_PRODUCT_OPTIONS.filter((opt) => selectedIds.includes(opt.id)).map(
    (opt) => opt.label
  );
  return labels.join(', ');
}

function formatLastUpdated(timestamp: number | undefined): string | null {
  if (!timestamp) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

function InfoTooltip({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-foreground-subtle hover:text-foreground-muted"
          aria-label={`More information about ${label}`}
          aria-expanded={open}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="max-w-[16rem] p-3 text-xs leading-relaxed text-foreground-muted"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <p>{text}</p>
      </PopoverContent>
    </Popover>
  );
}

function FieldLabel({ children, tooltip }: { children: ReactNode; tooltip: string }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        {children}
      </span>
      <InfoTooltip label={String(children)} text={tooltip} />
    </div>
  );
}

function DateRangeFieldLabel({ tooltip }: { tooltip: string }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <CalendarIcon className="h-3.5 w-3.5 text-foreground-subtle" aria-hidden />
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        Date range
      </span>
      <InfoTooltip label="Date range" text={tooltip} />
    </div>
  );
}

function CompareToFieldLabel({ tooltip }: { tooltip: string }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <CalendarIcon className="h-3.5 w-3.5 text-foreground-subtle" aria-hidden />
      <span className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        Compare to
      </span>
      <InfoTooltip label="Compare to" text={tooltip} />
    </div>
  );
}

function periodToDayPickerRange(range: DateRange): DayPickerDateRange | undefined {
  const from = isoToLocalDate(range.startDate);
  const to = isoToLocalDate(range.endDate);
  if (!from) return undefined;
  return { from, to: to ?? from };
}

function PeriodDateRangeBox({
  label,
  range,
  onApply,
  hideLabel = false,
}: {
  label: string;
  range: DateRange;
  onApply: (next: DateRange) => void;
  hideLabel?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DayPickerDateRange | undefined>();
  const displayValue = formatDateRangeLabel(range.startDate, range.endDate);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftRange(periodToDayPickerRange(range));
    }
    setOpen(nextOpen);
  };

  const draftStart = draftRange?.from ? localDateToIso(draftRange.from) : '';
  const draftEnd = draftRange?.to
    ? localDateToIso(draftRange.to)
    : draftRange?.from
      ? localDateToIso(draftRange.from)
      : '';
  const isDraftInvalid = Boolean(draftStart && draftEnd && draftEnd < draftStart);
  const canApply = Boolean(draftRange?.from && draftRange?.to && !isDraftInvalid);

  const handleApply = () => {
    if (!canApply || !draftRange?.from || !draftRange?.to) return;
    onApply({
      startDate: localDateToIso(draftRange.from),
      endDate: localDateToIso(draftRange.to),
    });
    setOpen(false);
  };

  return (
    <div className="min-w-0 w-full">
      {hideLabel ? null : (
        <div className="mb-1.5 text-xs font-medium text-foreground-muted">{label}</div>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-left"
            aria-label={`${label}: ${displayValue}. Open date picker.`}
            aria-haspopup="dialog"
          >
            <span className="min-w-0 flex-1 whitespace-nowrap text-sm font-medium text-foreground tabular-nums">{displayValue}</span>
            <CalendarIcon className="h-4 w-4 shrink-0 text-foreground-subtle" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={isoToLocalDate(range.startDate)}
            selected={draftRange}
            onSelect={setDraftRange}
            numberOfMonths={1}
          />
          <div className="space-y-2 border-t border-border px-3 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={draftStart}
                onChange={(event) => {
                  const from = isoToLocalDate(event.target.value);
                  if (!from) return;
                  setDraftRange((current) => ({
                    from,
                    to: current?.to && current.to >= from ? current.to : from,
                  }));
                }}
                aria-label={`${label} start date`}
                className="rounded-md border border-border px-2 py-1.5 text-sm"
              />
              <span className="text-sm text-foreground-subtle">to</span>
              <input
                type="date"
                value={draftEnd}
                onChange={(event) => {
                  const to = isoToLocalDate(event.target.value);
                  if (!to) return;
                  setDraftRange((current) => ({
                    from: current?.from ?? to,
                    to,
                  }));
                }}
                aria-label={`${label} end date`}
                className="rounded-md border border-border px-2 py-1.5 text-sm"
              />
            </div>
            {isDraftInvalid ? (
              <p className="text-sm text-error">End date must be on or after the start date.</p>
            ) : !draftRange?.to ? (
              <p className="text-sm text-foreground-muted">Select a start and end date.</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleApply} disabled={!canApply}>
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function PctChangeBadge({ pct }: { pct: ReturnType<typeof percentChangeVsBaseline> }) {
  if (pct.type === 'baseline_zero') {
    return <span className="text-sm font-medium text-foreground-muted">—</span>;
  }
  if (pct.type === 'both_zero') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-surface-muted px-2 py-0.5 text-xs font-semibold text-foreground-muted">
        <Minus className="h-3.5 w-3.5 shrink-0" aria-hidden />
        0.0%
      </span>
    );
  }

  const v = pct.value;
  const label = formatPctChangeLabel(pct);

  if (v < 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-error-tint px-2 py-0.5 text-xs font-semibold text-error-ink">
        <TrendingDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {label}
      </span>
    );
  }
  if (v > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-success-tint px-2 py-0.5 text-xs font-semibold text-success-ink">
        <TrendingUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-surface-muted px-2 py-0.5 text-xs font-semibold text-foreground-muted">
      <Minus className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {label}
    </span>
  );
}

export function SpReportCompareTab({
  profileId,
  accountName: _accountName,
}: {
  profileId: string;
  accountName?: string | null;
}) {
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const { periodA } = getPreviousMonthVsPriorMonth();
    return { ...periodA };
  });
  const [compareTo, setCompareTo] = useState<DateRange>(() => {
    const { periodB } = getPreviousMonthVsPriorMonth();
    return { ...periodB };
  });
  const [compareAdProductFilters, setCompareAdProductFilters] = useState<string[]>(() =>
    REPORT_AD_PRODUCT_OPTIONS.map((o) => o.id)
  );
  const compareAdProductsParam = useMemo(
    () => reportAdProductsQueryParam(compareAdProductFilters),
    [compareAdProductFilters]
  );

  const periodA = dateRange;
  const periodB = compareTo;

  const rangeAOk = isRangeValid(periodA);
  const rangeBOk = isRangeValid(periodB);
  const rangesOk = rangeAOk && rangeBOk;
  const comparisonSummary = formatComparisonRangeSummary(periodA, periodB);
  const adTypeSummary = selectedAdTypeLabels(compareAdProductFilters);
  const periodALabel = formatDateRangeLabel(periodA.startDate, periodA.endDate);
  const periodBLabel = formatDateRangeLabel(periodB.startDate, periodB.endDate);

  const toggleCompareAdProductFilter = (id: string) => {
    setCompareAdProductFilters((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev;
      if (has) return prev.filter((x) => x !== id);
      return [...prev, id].sort();
    });
  };

  const applyDateRange = useCallback((next: DateRange) => {
    setDateRange(next);
  }, []);

  const applyCompareTo = useCallback((next: DateRange) => {
    setCompareTo(next);
  }, []);

  const {
    data: dataA,
    isLoading: loadingA,
    isFetching: fetchingA,
    error: errorA,
    refetch: refetchA,
    dataUpdatedAt: updatedAtA,
  } = useQuery({
    queryKey: [
      'sp-report-summary-compare-a',
      profileId,
      periodA.startDate,
      periodA.endDate,
      compareAdProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getSpReportSummary(profileId, {
        startDate: periodA.startDate,
        endDate: periodA.endDate,
        adProducts: compareAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load summary for period A');
      return res.data;
    },
    enabled: !!profileId && rangesOk,
  });

  const {
    data: dataB,
    isLoading: loadingB,
    isFetching: fetchingB,
    error: errorB,
    refetch: refetchB,
    dataUpdatedAt: updatedAtB,
  } = useQuery({
    queryKey: [
      'sp-report-summary-compare-b',
      profileId,
      periodB.startDate,
      periodB.endDate,
      compareAdProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getSpReportSummary(profileId, {
        startDate: periodB.startDate,
        endDate: periodB.endDate,
        adProducts: compareAdProductsParam,
      });
      if (!res.success) throw new Error('Failed to load summary for period B');
      return res.data;
    },
    enabled: !!profileId && rangesOk,
  });

  const summaryA = useMemo(() => parseReportSummaryPayload(dataA), [dataA]);
  const summaryB = useMemo(() => parseReportSummaryPayload(dataB), [dataB]);

  const handleRefresh = useCallback(() => {
    void refetchA();
    void refetchB();
  }, [refetchA, refetchB]);

  const queriesEnabled = !!profileId && rangesOk;
  const isLoading = queriesEnabled && (loadingA || loadingB);
  const isRefreshing = queriesEnabled && (fetchingA || fetchingB);
  const hasError = queriesEnabled && Boolean(errorA || errorB);
  const showComparisonTable = queriesEnabled && !loadingA && !loadingB && !errorA && !errorB;
  const lastUpdated = formatLastUpdated(
    updatedAtA && updatedAtB ? Math.max(updatedAtA, updatedAtB) : undefined
  );

  return (
    <div className="mx-auto w-full max-w-[1380px] px-6 pb-8 pt-1">
      <header className="mb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold text-foreground">Compare</h1>
          <Badge variant="neutral" className="normal-case tracking-normal font-medium">
            Amazon
          </Badge>
          <span className="text-sm text-foreground-subtle">|</span>
          <span className="font-mono text-sm text-foreground-muted">{profileId}</span>
        </div>
      </header>

      <div className="mb-4 rounded-xl border border-border bg-surface p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[max-content_minmax(0,1fr)_minmax(0,1fr)_11.75rem] lg:gap-0">
          <section className="w-max max-w-full lg:pr-5">
            <FieldLabel tooltip="Select one or more Amazon ad types to include in the comparison.">
              Ad type
            </FieldLabel>
            <div className="flex flex-nowrap gap-2">
              {REPORT_AD_PRODUCT_OPTIONS.map((opt) => {
                const on = compareAdProductFilters.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleCompareAdProductFilter(opt.id)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                      on
                        ? 'border-[#4A779D] bg-[#4A779D] text-white'
                        : 'border-border bg-surface text-brand-ink hover:bg-page'
                    }`}
                  >
                    {on ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="min-w-0 lg:border-l lg:border-border lg:px-5">
            <DateRangeFieldLabel tooltip="The selected date range for Period A." />
            <PeriodDateRangeBox
              label="Date range"
              range={dateRange}
              onApply={applyDateRange}
              hideLabel
            />
          </section>

          <section className="min-w-0 lg:border-l lg:border-border lg:px-5">
            <CompareToFieldLabel tooltip="The baseline date range for comparison (Period B)." />
            <PeriodDateRangeBox
              label="Compare to"
              range={compareTo}
              onApply={applyCompareTo}
              hideLabel
            />
          </section>

          <section className="flex w-full shrink-0 flex-col items-stretch gap-1.5 self-start lg:mt-[1.75rem] lg:w-[11.75rem] lg:border-l lg:border-border lg:pl-5">
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={handleRefresh}
              disabled={!rangesOk || isRefreshing}
              className="w-full whitespace-nowrap"
            >
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden />
              )}
              {isRefreshing ? 'Refreshing…' : 'Refresh comparison'}
            </Button>
            {lastUpdated ? (
              <p className="text-center text-[11px] leading-snug text-foreground-subtle lg:text-left">
                Last updated: {lastUpdated}
              </p>
            ) : null}
          </section>
        </div>

        {!rangesOk ? (
          <p className="mt-4 text-sm text-error">
            Each range needs a start date on or before its end date.
          </p>
        ) : null}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-sm">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">Comparison results</h2>
          <p className="mt-1 text-base font-semibold text-foreground">{comparisonSummary}</p>
          <p className="mt-0.5 text-sm text-foreground-muted">{adTypeSummary}</p>
        </div>

        <div className="p-5">
          {!rangesOk ? (
            <p className="text-sm text-foreground-muted">
              Adjust the date ranges above to load a comparison.
            </p>
          ) : !profileId ? (
            <p className="text-sm text-foreground-muted">
              Select an account to load a comparison.
            </p>
          ) : isLoading ? (
            <CompareTableSkeleton periodALabel={periodALabel} periodBLabel={periodBLabel} />
          ) : hasError ? (
            <CompareErrorState onRetry={handleRefresh} />
          ) : showComparisonTable ? (
            <CompareTable
              summaryA={summaryA}
              summaryB={summaryB}
              periodALabel={periodALabel}
              periodBLabel={periodBLabel}
            />
          ) : null}
        </div>

        <div className="flex items-start gap-2 border-t border-border px-5 py-3">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground-subtle" aria-hidden />
          <p className="text-xs leading-relaxed text-foreground-subtle">
            Totals may be incomplete if more than 20,000 raw rows exist in a range (same limit as the
            Reports summary). The imported-rows line reflects row count returned for that window.
          </p>
        </div>
      </div>
    </div>
  );
}

function PeriodColumnHeader({
  title,
  dateLabel,
  dotClassName,
}: {
  title: string;
  dateLabel: string;
  dotClassName: string;
}) {
  return (
    <div className="inline-flex flex-col items-center gap-0.5 text-center">
      <span className="inline-flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClassName}`} aria-hidden />
        <span>{title}</span>
      </span>
      <span className="text-[10px] font-normal normal-case tracking-normal text-foreground-subtle">
        {dateLabel}
      </span>
    </div>
  );
}

function CompareTableSkeleton({
  periodALabel,
  periodBLabel,
}: {
  periodALabel: string;
  periodBLabel: string;
}) {
  return (
    <div
      className="overflow-x-auto rounded-lg border border-border"
      aria-busy="true"
      aria-label="Loading comparison"
    >
      <table className="w-full min-w-[36rem] border-collapse">
        <thead>
          <tr className="border-b border-border bg-surface-muted/40">
            <th className="w-[26%] px-4 py-3 text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              Metric
            </th>
            <th className="w-[22%] px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              <PeriodColumnHeader title="Period A" dateLabel={periodALabel} dotClassName="bg-brand" />
            </th>
            <th className="w-[22%] px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              <PeriodColumnHeader
                title="Period B (baseline)"
                dateLabel={periodBLabel}
                dotClassName="bg-foreground-subtle"
              />
            </th>
            <th className="px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              % Change
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {REPORT_COMPARE_METRICS.map((def) => (
            <tr key={def.key}>
              <td className="px-4 py-3.5">
                <span className="block h-4 w-28 animate-pulse rounded bg-surface-muted" />
              </td>
              <td className="px-4 py-3.5">
                <span className="ml-auto block h-4 w-20 animate-pulse rounded bg-surface-muted" />
              </td>
              <td className="px-4 py-3.5">
                <span className="ml-auto block h-4 w-20 animate-pulse rounded bg-surface-muted" />
              </td>
              <td className="px-4 py-3.5">
                <span className="ml-auto block h-5 w-14 animate-pulse rounded-md bg-surface-muted" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompareErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-error/20 bg-error-tint/30 px-4 py-8 text-center">
      <h4 className="text-sm font-semibold text-foreground">Unable to load comparison</h4>
      <p className="mt-2 text-sm text-foreground-muted">
        Something went wrong while loading the comparison data.
      </p>
      <Button type="button" variant="secondary" size="sm" onClick={onRetry} className="mt-4">
        Try again
      </Button>
    </div>
  );
}

function CompareTable({
  summaryA,
  summaryB,
  periodALabel,
  periodBLabel,
}: {
  summaryA: ReportSummaryParsed;
  summaryB: ReportSummaryParsed;
  periodALabel: string;
  periodBLabel: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[36rem] border-collapse">
        <thead>
          <tr className="border-b border-border bg-surface-muted/40">
            <th className="w-[26%] px-4 py-3 text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              Metric
            </th>
            <th className="w-[22%] px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              <div className="flex justify-end">
                <PeriodColumnHeader
                  title="Period A"
                  dateLabel={periodALabel}
                  dotClassName="bg-brand"
                />
              </div>
            </th>
            <th className="w-[22%] px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              <div className="flex justify-end">
                <PeriodColumnHeader
                  title="Period B (baseline)"
                  dateLabel={periodBLabel}
                  dotClassName="bg-foreground-subtle"
                />
              </div>
            </th>
            <th className="px-4 py-3 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground-muted">
              <span className="inline-flex items-center justify-end gap-1">
                % Change
                <InfoTooltip
                  label="% Change"
                  text="Percentage change is calculated as (Period A − Period B) ÷ Period B."
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {REPORT_COMPARE_METRICS.map((def) => (
            <CompareRow key={def.key} def={def} summaryA={summaryA} summaryB={summaryB} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompareRow({
  def,
  summaryA,
  summaryB,
}: {
  def: CompareMetricDef;
  summaryA: ReportSummaryParsed;
  summaryB: ReportSummaryParsed;
}) {
  const a = getMetricValue(summaryA, def);
  const b = getMetricValue(summaryB, def);
  const pct = a !== null && b !== null ? percentChangeVsBaseline(a, b) : null;

  return (
    <tr className="hover:bg-surface-muted/30">
      <td className="px-4 py-3.5 text-sm font-semibold text-foreground">
        <span className="inline-flex items-center gap-1.5">
          {def.label}
          {def.useRowCount ? (
            <InfoTooltip
              label="Imported rows in range"
              text="Number of raw rows imported for the selected date range. Totals may be incomplete when the source contains more than 20,000 raw rows."
            />
          ) : null}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm font-semibold tabular-nums text-foreground">
        {formatMetricCell(def, a)}
      </td>
      <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm font-medium tabular-nums text-foreground-muted">
        {formatMetricCell(def, b)}
      </td>
      <td className="px-4 py-3.5 text-right">
        {pct !== null ? (
          <PctChangeBadge pct={pct} />
        ) : (
          <span className="text-sm text-foreground-muted">—</span>
        )}
      </td>
    </tr>
  );
}
